<template>
  <q-dialog
    ref="dialog"
  >
    <q-card
      class="dialog-card"
    >
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ cancellationTitle || $t('scheduling.cancelDialog.dialogTitle') }}
        </div>
        <div class="col-auto">
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            @click="hide"
          />
        </div>
      </q-card-section>
      <q-form
        ref="form"
        @submit="onSubmitForm"
      >
        <q-card-section class="row q-col-gutter-sm">
          <q-select
            :model-value="editCancellationReason"
            :options="cancellationReasonsOptions"
            :label="$t('scheduling.cancelDialog.cancellationReasonLabel')"
            map-options
            outlined
            hide-bottom-space
            :rules="rules.cancellationReason"
            class="col-12"
            @update:model-value="onSetCancellationReason"
          />
          <q-select
            v-if="canSelectCancellationType"
            v-model="editCancellationType"
            :options="cancellationTypesOptions"
            :label="$t('scheduling.cancelDialog.cancellationTypeLabel')"
            emit-value
            map-options
            outlined
            hide-bottom-space
            :rules="rules.cancellationTypes"
            class="col-12"
          />
          <q-checkbox
            v-if="allowCreateUnassignedAppointment && canShowCreateUnassignedAppointment"
            v-model="shouldCreateUnassignedAppointment"
            :label="$t('scheduling.cancelDialog.createUnassignedAppointmentCheckboxTitle')"
            color="primary"
          />
          <notify-staff-checkbox
            v-model="notifyStaff"
            :availability="notifyStaffAvailability"
            :captions="notifyStaffCaptions"
          />
        </q-card-section>
        <q-card-actions class="row">
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
            @click="hide"
          />
          <q-btn
            flat
            :label="$t('common.buttonSubmit')"
            type="submit"
            color="primary"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { chain, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import NotifyStaffCheckbox from 'components/Appointments/NotifyStaffCheckbox.vue';
import { CANCELLATION_TYPES } from 'src/constants/appointments';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import {
  NOTIFY_STAFF_AVAILABILITY,
  isNotifyStaffDefaultChecked,
  isNotifyStaffDisabled,
  isNotifyStaffShown,
} from 'src/utils/NotificationAvailability';

export default {
  name: 'CancelAppointmentDialog',

  components: { NotifyStaffCheckbox },

  mixins: [Interactions],

  props: {
    allowCreateUnassignedAppointment: {
      type: Boolean,
      default: false,
    },
    cancellationReason: {
      type: String,
      default: null,
    },
    cancellationType: {
      type: String,
      default: null,
    },
    blockCancellationTypes: {
      type: Array,
      default: () => [],
    },
    cancellationTitle: {
      type: String,
      default: null,
    },
    // Whether the affected staff would actually see the notification - see NotifyStaffCheckbox.vue.
    notifyStaffAvailability: {
      type: String,
      default: 'none',
      validator: (value) => NOTIFY_STAFF_AVAILABILITY.includes(value),
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      editCancellationReason: null,
      editCancellationReasonLabel: null,
      editCancellationType: null,
      shouldCreateUnassignedAppointment: false,
      notifyStaff: isNotifyStaffDefaultChecked(this.notifyStaffAvailability),

      rules: {
        cancellationReason: [
          (val) => !!val || this.$t('scheduling.cancelDialog.validateReason'),
        ],
        cancellationTypes: [
          (val) => isEmpty(this.cancellationTypesOptions)
          || !isEmpty(val)
          || this.$t('scheduling.cancelDialog.validateType'),
        ],
      },
    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic', 'cancellationReasons']),

    canSelectCancellationType() {
      return this.selectedCancellationReason?.cancellationTypes?.length > 1;
    },

    canShowCreateUnassignedAppointment() {
      return this.editCancellationType === CANCELLATION_TYPES.STAFF;
    },

    // The same predicates NotifyStaffCheckbox renders from, so onSubmitForm can guard the emitted
    // notifyStaff against a state the reader never saw.
    showNotifyStaffCheckbox() {
      return isNotifyStaffShown(this.notifyStaffAvailability);
    },

    notifyStaffDisabled() {
      return isNotifyStaffDisabled(this.notifyStaffAvailability);
    },

    notifyStaffCaptions() {
      return {
        disabledSelf: this.$t('scheduling.cancelDialog.notifyStaffDisabledSelfCaption'),
        disabledUnsaved: this.$t('scheduling.cancelDialog.notifyStaffDisabledUnsavedCaption'),
        disabledNoChange: this.$t('scheduling.cancelDialog.notifyStaffDisabledNoChangeCaption'),
        partialNoChange: this.$t('scheduling.cancelDialog.notifyStaffPartialNoChangeCaption'),
      };
    },

    availableCancellationReasons() {
      return this.cancellationReasons
        .map((cancellationReason) => ({
          ...cancellationReason,
          cancellationTypes: cancellationReason.cancellationTypes.filter(
            (type) => !this.blockCancellationTypes.includes(type),
          ),
        }))
        .filter((cancellationReason) => !isEmpty(cancellationReason.cancellationTypes));
    }, // availableCancellationReasons()

    selectedCancellationReason() {
      return this.availableCancellationReasons.find(
        (cancellationReason) => cancellationReason.id === this.editCancellationReason,
      );
    },

    cancellationReasonsOptions() {
      return chain(this.availableCancellationReasons)
        .map(
          (cancellationReason) => ({ label: cancellationReason.cancellationReason, value: cancellationReason.id }),
        )
        .sortBy((items) => items.label.toLowerCase())
        .value();
    }, // cancellationReasonsOptions()

    cancellationTypesOptions() {
      return chain(this.$tm('cancellationTypes'))
        .map((label, value) => ({ label, value }))
        .filter((option) => this.selectedCancellationReason?.cancellationTypes.includes(option.value))
        .value();
    }, // cancellationTypesOptions()

  },

  watch: {
    editCancellationType: {
      handler() {
        if (this.editCancellationType !== CANCELLATION_TYPES.STAFF) {
          this.shouldCreateUnassignedAppointment = false;
        }
      },
    },
  }, // watch

  methods: {

    onSetCancellationReason({ value, label }) {
      if (this.editCancellationReason === value) {
        return;
      }
      this.editCancellationReason = value;
      this.editCancellationReasonLabel = label;
      if (this.cancellationTypesOptions.length === 1) {
        this.editCancellationType = this.cancellationTypesOptions[0].value;
      } else {
        this.editCancellationType = null;
      }
    },

    show() {
      this.editCancellationReason = this.cancellationReason;
      this.editCancellationType = this.cancellationType;

      this.$refs.dialog.show();
    }, // show()

    hide() {
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onSubmitForm() {
      this.$emit('ok', {
        cancellationReason: this.editCancellationReason || null,
        cancellationType: this.editCancellationType || null,
        cancellationReasonLabel: this.editCancellationReasonLabel || null,
        shouldCreateUnassignedAppointment: this.shouldCreateUnassignedAppointment || false,
        notifyStaff: this.showNotifyStaffCheckbox && !this.notifyStaffDisabled && this.notifyStaff,
      });
      this.hide();
    }, // onSubmitForm()

  },
};
</script>
<style lang="scss" scoped>
.dialog-card {
  width: 500px;
}
</style>
